import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import type { EquityCurvePoint, ExcludedSignal, SignalTrackRecordSummary } from '../../types'
import { signalsAPI, trackRecordAPI } from '../../services/api'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../../config/glossary'
import LevelExitsNote from '../LevelExitsNote'
import Phase2TrialNote from '../Phase2TrialNote'
import ResultsDisclaimer from '../ResultsDisclaimer'
import Tooltip from '../Tooltip'
import { drawdownLine } from '../../utils/drawdown'
import { formatPnlPct } from '../../utils/pnl'
import { parseApiDate } from '../../utils/signalExpiry'
import TerminalPanel from '../dashboard/TerminalPanel'
import CallLink from '../signals/CallLink'
import { VERIFY_PATH } from '../../routes'
import EquityCurveChart, { curveOutcome, maxDrawdown, toCurvePoints } from '../dashboard/EquityCurveChart'
import DirectionSplitPanel from './DirectionSplitPanel'
import AssetSplitPanel from './AssetSplitPanel'
import ExcludedSignalsPanel from './ExcludedSignalsPanel'
import EnginePausesPanel from './EnginePausesPanel'
import EngineVersionsPanel from './EngineVersionsPanel'
import {
  AvgRTile,
  ClosedColumn,
  ClosedTradesPanel,
  DrawdownReadout,
  EmptyBox,
  ErrorState,
  LoadingState,
  OutcomeBadge,
  PREVIEW_ROWS,
  StatTile,
  WinRateTile,
  formatR,
  pnlClass,
} from './recordUI'
import { displaySymbol } from '../../utils/displaySymbol'
import { timeInTradeLine } from '../../utils/timeInTrade'

/**
 * Every figure here is gross, before trading fees and slippage (decided
 * 2026-10-09): stored signal P&L is the raw price move -- services/trading.py
 * closes at the crossing price and records no fill cost -- and nothing a user
 * sees assumes a fee. A reader's own fee is applied only on the fee
 * calculator, which ResultsDisclaimer links to. Nothing in this view may net
 * a figure or carry a fee rate.
 */
const mean = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null)

interface ClosedRow {
  n: number
  point: EquityCurvePoint
  win: boolean | null
  time: Date | null
}

const when = (row: ClosedRow) => (row.time ? format(row.time, 'MMM d, yyyy HH:mm') : '—')

const COLUMNS: ClosedColumn<ClosedRow>[] = [
  { header: '#', render: (row) => row.n, cellClass: () => 'font-mono text-gray-500' },
  { header: 'Closed', render: when, cellClass: () => 'font-mono tabular-nums text-gray-400' },
  {
    header: 'Signal',
    render: (row) => (
      <>
        <span className="font-semibold text-gray-100">{displaySymbol(row.point.symbol)}</span>{' '}
        <span className="text-xs uppercase text-gray-400">{row.point.signal_type}</span>
      </>
    ),
  },
  { header: 'Outcome', render: (row) => <OutcomeBadge win={row.win} /> },
  {
    header: 'Result (before fees)',
    align: 'right',
    render: (row) => formatPnlPct(row.point.profit_loss_pct),
    cellClass: (row) => pnlClass(row.point.profit_loss_pct),
  },
  {
    header: 'R (before fees)',
    align: 'right',
    render: (row) => (row.point.r_multiple != null ? formatR(row.point.r_multiple) : '—'),
    cellClass: (row) => (row.point.r_multiple != null ? pnlClass(row.point.r_multiple) : 'text-gray-500'),
  },
  {
    header: 'Permalink',
    align: 'right',
    render: (row) => (row.point.signal_id != null ? <CallLink id={row.point.signal_id} /> : '—'),
  },
]

/**
 * The signal engine's automated record: every closed real signal, demo and
 * paper excluded server-side.
 *
 * Two sources, split so that no number has two implementations.
 *
 * GET /api/signals/equity-curve owns the series, the closed list, the
 * win/loss counts and the rate taken from them -- a rate computed from the
 * counts printed beside it cannot disagree with them. Both running totals come
 * from the response rather than being re-summed here.
 *
 * GET /api/track-record/summary owns the statistics: mean gross R and the
 * win rate's confidence interval. Meaning the per-point R values here would
 * be a second implementation of the mean, and it would not even agree -- the
 * points are published rounded. When the summary is unavailable the mean says
 * so instead of a second opinion being invented for it; the series, the
 * counts and the list are unaffected.
 *
 * Both are fetched without a fee, so both are gross.
 */
const EngineRecordView = () => {
  const [curve, setCurve] = useState<EquityCurvePoint[] | null>(null)
  const [summary, setSummary] = useState<SignalTrackRecordSummary | null>(null)
  const [failed, setFailed] = useState(false)
  const [reloads, setReloads] = useState(0)
  const reload = () => setReloads((n) => n + 1)
  useEffect(() => {
    let active = true
    setFailed(false)
    signalsAPI
      .equityCurve(3650)
      .then((data) => {
        if (active) setCurve(data)
      })
      .catch((error) => {
        console.error('Track record failed to load:', error)
        if (active) setFailed(true)
      })
    Promise.resolve(trackRecordAPI.getSignalSummary?.())
      .then((data) => {
        if (active) setSummary(data ?? null)
      })
      .catch((error) => {
        // The interval, avg R and per-direction figures drop out; the rest still stands.
        console.error('Track record summary failed to load:', error)
      })
    return () => {
      active = false
    }
  }, [reloads])
  // Excluded signals are out of the curve and every figure above; they are
  // listed from their own endpoint, only when the summary says there are any.
  const excludedCount = summary?.excluded_signals ?? 0
  const [excluded, setExcluded] = useState<ExcludedSignal[]>([])
  useEffect(() => {
    if (excludedCount <= 0) {
      setExcluded([])
      return
    }
    let active = true
    Promise.resolve(trackRecordAPI.getExcluded?.())
      .then((rows) => {
        if (active) setExcluded(rows ?? [])
      })
      .catch((error) => console.error('Excluded signals failed to load:', error))
    return () => {
      active = false
    }
  }, [excludedCount])


  const stats = useMemo(() => {
    const rows = curve ?? []
    const outcomes = rows.map(curveOutcome)
    const winMoves = rows.filter((_, i) => outcomes[i] === true).map((p) => p.profit_loss_pct)
    const lossMoves = rows.filter((_, i) => outcomes[i] === false).map((p) => p.profit_loss_pct)
    const closed = rows.length
    const points = toCurvePoints(rows)
    // The total is the API's own running sum, read off the last point, so the
    // tile, the curve and the response cannot drift apart.
    const last = rows[closed - 1]
    const gross = last ? last.cumulative_pnl_pct : 0
    return {
      closed,
      wins: winMoves.length,
      losses: lossMoves.length,
      breakevens: closed - winMoves.length - lossMoves.length,
      // Same formula as the backend summary: breakevens stay in the denominator.
      winRate: closed ? (winMoves.length / closed) * 100 : null,
      avgWin: mean(winMoves),
      avgLoss: mean(lossMoves),
      gross,
      points,
      drawdown: maxDrawdown(points),
    }
  }, [curve])

  // Newest first; #n stays the close-order number the chart uses.
  const rows = useMemo<ClosedRow[]>(
    () =>
      (curve ?? [])
        .map((point, index) => ({
          n: index + 1,
          point,
          win: curveOutcome(point),
          time: point.timestamp ? parseApiDate(point.timestamp) : null,
        }))
        .reverse(),
    [curve]
  )

  if (failed && curve == null)
    return <ErrorState message="The track record is unavailable right now." onRetry={reload} />
  if (curve == null) return <LoadingState label="Loading the signal engine's record…" />

  const { closed, wins, losses, breakevens } = stats

  return (
    <>
      <section data-testid="gross-record" aria-labelledby="gross-record-title">
        <h2 id="gross-record-title" className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">
          <Tooltip content={GLOSSARY.grossRecord}>Signal record · before fees</Tooltip>
          {excludedCount > 0 && (
            <span className="ml-2 normal-case tracking-normal text-amber-300" data-testid="excluded-count">
              · {excludedCount} excluded, listed below
            </span>
          )}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <WinRateTile
            winRate={stats.winRate}
            closed={closed}
            wins={wins}
            losses={losses}
            breakevens={breakevens}
            noun="closed"
            interval={summary?.win_rate_interval}
          />

          <StatTile
            label="Avg win"
            hint={GLOSSARY.avgWin}
            testId="stat-avg-win"
            value={stats.avgWin == null ? '—' : formatPnlPct(stats.avgWin)}
            valueClass={stats.avgWin == null ? 'text-gray-500' : 'text-green-400'}
          >
            before fees, per winning signal · {wins} win{wins === 1 ? '' : 's'}
          </StatTile>

          <StatTile
            label="Avg loss"
            hint={GLOSSARY.avgLoss}
            testId="stat-avg-loss"
            value={stats.avgLoss == null ? '—' : formatPnlPct(stats.avgLoss)}
            valueClass={stats.avgLoss == null ? 'text-gray-500' : 'text-red-400'}
          >
            before fees, per losing signal · {losses} loss{losses === 1 ? '' : 'es'}
          </StatTile>

          <AvgRTile
            // The backend's gross mean, never one taken again from the rounded points.
            avgR={summary?.avg_r_gross ?? null}
            basis={summary ? 'before fees · per closed signal' : 'unavailable right now'}
          >
            {timeInTradeLine(summary?.time_in_trade) && (
              <p data-testid="stat-time-in-trade">
                <Tooltip content={GLOSSARY.timeInTrade}>{timeInTradeLine(summary?.time_in_trade)}</Tooltip>
              </p>
            )}
            {drawdownLine(summary?.drawdown) && (
              <p data-testid="stat-drawdown-r">
                <Tooltip content={GLOSSARY.drawdownR}>{drawdownLine(summary?.drawdown)}</Tooltip>
              </p>
            )}
          </AvgRTile>

          <StatTile
            label="Sum of trade returns"
            hint={GLOSSARY.sumOfTradeReturns}
            testId="stat-gross-sum"
            value={closed ? formatPnlPct(stats.gross) : '—'}
            valueClass={closed ? pnlClass(stats.gross) : 'text-gray-500'}
          >
            {closed ? 'before fees · unsized, not account return' : 'no closed signals yet'}
          </StatTile>
        </div>
        <ResultsDisclaimer className="mt-2" />
        <LevelExitsNote className="mt-1" />
        <Phase2TrialNote className="mt-1" />
      </section>

      {/* The closes themselves come straight after the headline figures they
          add up to: five newest, the rest one labelled click away. */}
      <ClosedTradesPanel
        title="Closed signals"
        total={closed}
        rows={rows}
        rowKey={(row) => row.n}
        columns={COLUMNS}
        previewRows={PREVIEW_ROWS}
        win={(row) => row.win}
        empty={<p className="py-8 text-center text-sm text-gray-500">No closed signals yet.</p>}
        mobileTitle={(row) => (
          <>
            <span className="font-mono text-gray-500">#{row.n}</span>{' '}
            <span className="font-semibold text-gray-100">{displaySymbol(row.point.symbol)}</span>{' '}
            <span className="text-xs uppercase text-gray-400">{row.point.signal_type}</span>
            {row.point.signal_id != null && (
              <>
                {' '}
                <CallLink id={row.point.signal_id} className="text-xs" />
              </>
            )}
          </>
        )}
        mobileDetail={(row) => (
          <>
            <span>{when(row)}</span>
            <span>
              <span className={pnlClass(row.point.profit_loss_pct)}>{formatPnlPct(row.point.profit_loss_pct)}</span>
              {row.point.r_multiple != null && ` · ${formatR(row.point.r_multiple)}`} before fees
            </span>
          </>
        )}
      />

      {/* All / Longs / Shorts, from the summary: absent (not guessed) when it is unavailable. */}
      {summary && closed > 0 && <DirectionSplitPanel summary={summary} />}

      {/* By coin, from the summary: absent on an older API build. */}
      {summary?.closed_by_symbol && summary.closed_by_symbol.length > 0 && (
        <AssetSplitPanel records={summary.closed_by_symbol} />
      )}

      <TerminalPanel
        title="Equity curve · before fees"
        tone="indigo"
        subtitle={
          <p className="text-xs text-gray-500">
            Each closed signal's result added up in close order: not smoothed, not compounded.
          </p>
        }
        action={closed > 0 ? <DrawdownReadout value={stats.drawdown} unit="points" /> : undefined}
      >
        <div className="h-64 md:h-80">
          {closed === 0 ? (
            <EmptyBox title="No closed signals yet" detail="The first close, win or loss, starts the record." />
          ) : (
            <EquityCurveChart
              points={stats.points}
              ariaLabel={`Equity curve over ${closed} closed signals: ${formatPnlPct(
                stats.gross
              )} before fees, ${wins} wins, ${losses} losses, max drawdown ${stats.drawdown.toFixed(2)} points`}
            />
          )}
        </div>
      </TerminalPanel>

      {excluded.length > 0 && <ExcludedSignalsPanel rows={excluded} />}
      <EnginePausesPanel />
      <EngineVersionsPanel />

      {/* The record's footer: where anyone can check it independently. */}
      <p className="text-sm text-gray-400" data-testid="verify-record">
        Every call here is fingerprinted and published daily.{' '}
        <Link to={VERIFY_PATH} className="text-accent hover:underline">
          Verify this record →
        </Link>
      </p>
    </>
  )
}

export default EngineRecordView
